import { useQuery } from '@tanstack/react-query'
import { useState, type ReactNode } from 'react'

import { useAuth } from '../AuthContext'
import {
  emergencyHelpUsageKey,
  fetchEmergencyHelpUsage,
  type KindCounts,
  type UsageWindow,
} from '../api/emergencyHelpUsage'

/**
 * Emergency Help usage on the AMTC dashboard (M35-T07, ADR-0064 decision 4).
 *
 * Anonymous counts only: how often the flow was opened, 112 was called, and a
 * partner hospital was viewed, called or asked for directions. Next to the M36
 * feedback list they show how many people that list will never reach.
 *
 * Public at `/emergency` since M36's switch-over (M36-T11): these are real
 * people's visits now, so the "test traffic only" heading is gone.
 */

const KIND_ROWS: ReadonlyArray<{ kind: keyof KindCounts; label: string }> = [
  { kind: 'opened', label: 'Opened Emergency Help' },
  { kind: 'called_112', label: 'Tapped Call 112' },
  { kind: 'viewed_hospital', label: 'Viewed a partner hospital' },
  { kind: 'called_hospital', label: 'Tapped Call this hospital' },
  { kind: 'got_directions', label: 'Asked for directions' },
]

const TOP_HOSPITALS = 5

export function EmergencyHelpUsageCard(): ReactNode {
  const { token } = useAuth()
  const [days, setDays] = useState<UsageWindow>(7)
  const query = useQuery({
    queryKey: emergencyHelpUsageKey(days),
    queryFn: () => fetchEmergencyHelpUsage(token, days),
    enabled: Boolean(token),
  })

  return (
    <section
      aria-labelledby="eh-usage-heading"
      className="rounded-2xl border border-line-1 bg-bg-app p-6"
    >
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2
            id="eh-usage-heading"
            className="font-display text-lg font-semibold text-ink-1"
          >
            Emergency Help usage
          </h2>
          <p className="mt-1 text-sm text-ink-3">
            Counts only. No one&apos;s details are recorded.
          </p>
        </div>
        <div
          role="group"
          aria-label="Period"
          className="inline-flex rounded-[8px] border border-line-2 p-0.5"
        >
          {([7, 30] as const).map((d) => (
            <button
              key={d}
              type="button"
              aria-pressed={days === d}
              onClick={() => setDays(d)}
              className={`h-8 rounded-[6px] px-3 text-sm font-medium focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent ${
                days === d
                  ? 'bg-accent text-accent-on'
                  : 'text-ink-2 hover:bg-bg-soft'
              }`}
            >
              Last {d} days
            </button>
          ))}
        </div>
      </header>
      <hr className="mt-3 border-line-1" />

      {query.isPending && (
        <div role="status" aria-label="Loading usage counts" className="mt-4">
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className="mb-3 h-4 w-full animate-pulse rounded bg-bg-sunken"
            />
          ))}
        </div>
      )}

      {query.isError && (
        <div
          role="alert"
          className="mt-4 flex items-center justify-between gap-4"
        >
          <p className="text-sm text-bed-bad">Could not load usage counts.</p>
          <button
            type="button"
            onClick={() => void query.refetch()}
            className="inline-flex h-8 items-center rounded-[8px] border border-line-2 px-3 text-sm font-medium text-ink-1 hover:bg-bg-soft"
          >
            Retry
          </button>
        </div>
      )}

      {query.isSuccess && (
        <p className="mt-4 text-sm text-ink-2">
          Today so far (UTC):{' '}
          <span className="font-semibold tabular-nums text-ink-1">
            {query.data.by_day.at(-1)?.total ?? 0}
          </span>{' '}
          events
        </p>
      )}

      {query.isSuccess && (
        <div className="mt-4 grid gap-6 [&>*]:min-w-0 md:grid-cols-2">
          <table className="w-full border-collapse text-sm">
            <caption className="sr-only">
              Emergency Help events in the last {query.data.days} days, by kind
            </caption>
            <tbody>
              {KIND_ROWS.map(({ kind, label }) => (
                <tr
                  key={kind}
                  className="border-b border-line-1 last:border-b-0"
                >
                  <th
                    scope="row"
                    className="py-2 pr-3 text-left font-normal text-ink-2"
                  >
                    {label}
                  </th>
                  <td className="py-2 text-right font-semibold tabular-nums text-ink-1">
                    {query.data.totals[kind]}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div>
            <h3 className="text-sm font-semibold text-ink-1">Top hospitals</h3>
            {query.data.hospitals.length === 0 ? (
              <p className="mt-2 text-sm text-ink-3">
                No hospital was viewed or called in this period.
              </p>
            ) : (
              <table className="mt-2 w-full border-collapse text-sm">
                <caption className="sr-only">
                  Partner hospitals viewed and called in the last{' '}
                  {query.data.days} days
                </caption>
                <thead>
                  <tr className="text-left text-xs text-ink-3">
                    <th scope="col" className="py-1 pr-3 font-medium">
                      Hospital
                    </th>
                    <th scope="col" className="py-1 text-right font-medium">
                      Viewed
                    </th>
                    <th
                      scope="col"
                      className="py-1 pl-3 text-right font-medium"
                    >
                      Called
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {query.data.hospitals.slice(0, TOP_HOSPITALS).map((h) => (
                    <tr key={h.slug} className="border-t border-line-1">
                      <th
                        scope="row"
                        className="py-2 pr-3 text-left font-normal text-ink-1"
                      >
                        {h.name}
                      </th>
                      <td className="py-2 text-right tabular-nums text-ink-1">
                        {h.viewed}
                      </td>
                      <td className="py-2 pl-3 text-right tabular-nums text-ink-1">
                        {h.called}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}
    </section>
  )
}
